import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Accordion } from 'react-bootstrap';
import { tableAPI, mgbSetupAPI, recordAPI } from '../api/api';
import './FieldConfigPanel.css';

function FieldConfigPanel({ tableName, tableID, fields, show, onHide, onSave }) {
  const [fieldSettings, setFieldSettings] = useState({});
  const [displayNames, setDisplayNames] = useState({});
  const [fieldLinks, setFieldLinks] = useState({});
  const [subRecordIDs, setSubRecordIDs] = useState({});
  const [sqlDisplays, setSqlDisplays] = useState({});
  const [loadedFields, setLoadedFields] = useState([]);
  const [allTables, setAllTables] = useState([]);
  const [allXbyXConfigs, setAllXbyXConfigs] = useState([]);
  const [tableFieldsMap, setTableFieldsMap] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Use a ref to store tableID so it's immediately available in handleSave
  const tableIDRef = useRef(tableID);

  useEffect(() => {
    if (show && tableName) {
      loadFieldVisibility();
    }
  }, [show, tableName]);

  const loadFieldVisibility = async () => {
    try {
      setLoading(true);
      
      // Load fields from API if not provided
      let fieldsToUse = fields;
      let tableIDToUse = tableID;
      
      // If no tableID provided, try to get it from mgbSetup tables
      if (!tableIDToUse) {
        try {
          const tablesRes = await mgbSetupAPI.getTables();
          if (tablesRes.data.success && tablesRes.data.tables) {
            const registeredTable = tablesRes.data.tables.find(t => t.TableName === tableName);
            if (registeredTable) {
              tableIDToUse = registeredTable.tableID;
              
              // Try to load fields from MGB_Fields metadata (includes newly added fields)
              try {
                const mmbFieldsRes = await mgbSetupAPI.getTableFieldsList(tableIDToUse);
                if (mmbFieldsRes.data.success && mmbFieldsRes.data.fields && mmbFieldsRes.data.fields.length > 0) {
                  fieldsToUse = mmbFieldsRes.data.fields;
                }
              } catch (mmbErr) {
                // Fallback to table schema on error
              }
            }
          }
        } catch (err) {
          // Continue with other methods if fetching registered tables fails
        }
      }
      
      if (!fieldsToUse || fieldsToUse.length === 0) {
        const fieldsRes = await tableAPI.getTableFields(tableName);
        if (fieldsRes.data.success) {
          fieldsToUse = fieldsRes.data.fields;
          setLoadedFields(fieldsRes.data.fields);
          // Extract tableID from first field if available
          if (fieldsRes.data.fields && fieldsRes.data.fields.length > 0) {
            const firstField = fieldsRes.data.fields[0];
            tableIDToUse = firstField.tableID || firstField.TableID || firstField.table_id;
          }
          // Initialize display names from fields
          const names = {};
          fieldsRes.data.fields.forEach(f => {
            names[f.fieldName] = f.displayName || f.fieldName;
          });
          setDisplayNames(names);
        }
      } else {
        setLoadedFields(fieldsToUse);
        // Extract tableID from first field if not provided
        if (!tableIDToUse && fieldsToUse && fieldsToUse.length > 0) {
          const firstField = fieldsToUse[0];
          tableIDToUse = firstField.tableID || firstField.TableID || firstField.table_id;
        }
        const names = {};
        const subRecords = {};
        const sqlDisp = {};
        fieldsToUse.forEach(f => {
          const fieldKey = f.fieldname || f.fieldName;
          names[fieldKey] = f.displayName || fieldKey;
          subRecords[fieldKey] = f.subRecordID || null;
          sqlDisp[fieldKey] = f.SQLDisplay || f.sqlDisplay || '';
        });
        setDisplayNames(names);
        setSubRecordIDs(subRecords);
        setSqlDisplays(sqlDisp);
      }
      
      // Store resolved tableID in ref for use in handleSave
      tableIDRef.current = tableIDToUse;
      
      // If still no tableID, try one more time from mgbSetup with better error handling
      if (!tableIDRef.current) {
        try {
          const fallbackRes = await mgbSetupAPI.getTables();
          if (fallbackRes.data && fallbackRes.data.tables) {
            const table = fallbackRes.data.tables.find(t => 
              t.TableName === tableName || t.table1 === tableName
            );
            if (table && table.tableID) {
              tableIDRef.current = table.tableID;
            }
          }
        } catch (fallbackErr) {
          // Fallback fetch failed, continue
        }
      }
      
      // Initialize field settings from the loaded fields (which now have shortViewable from DB)
      let settings = {};
      if (fieldsToUse && fieldsToUse.length > 0) {
        fieldsToUse.forEach(field => {
          // Use shortViewable from the field metadata that was loaded from the database
          settings[field.fieldname] = {
            isVisible: field.shortViewable !== false && field.shortViewable !== 0,
            columnOrder: field.ordering || 0
          };
        });
      }
      
      // Load field links (don't wait for other non-critical data)
      let links = {};
      try {
        const linksRes = await tableAPI.getFieldLinks(tableName);
        if (linksRes.data.success && linksRes.data.fieldLinks) {
          linksRes.data.fieldLinks.forEach(fl => {
            links[fl.FieldName] = {
              isLinked: fl.IsLinked === 1 || fl.IsLinked === true,
              linkedTableName: fl.LinkedTableName,
              linkedFieldName: fl.LinkedFieldName
            };
          });
        }
      } catch (err) {
        // Error loading field links, continue
      }
      
      // Set defaults for fields without visibility settings
      if (fieldsToUse && fieldsToUse.length > 0) {
        fieldsToUse.forEach(field => {
          if (!links[field.fieldname]) {
            links[field.fieldname] = {
              isLinked: false,
              linkedTableName: null,
              linkedFieldName: null
            };
          }
        });
      }
      
      setFieldSettings(settings);
      setFieldLinks(links);
      
      // Load non-critical data in parallel after main data is loaded
      // Load all tables for linking dropdown
      tableAPI.getAllTables().then(tablesRes => {
        if (tablesRes.data.success && tablesRes.data.tables) {
          const tableNames = tablesRes.data.tables
            .map(t => typeof t === 'string' ? t : t.tableName)
            .filter(t => t !== tableName);
          setAllTables(tableNames);
          
          // Load fields for each table in parallel (limited concurrency)
          const fieldsMap = {};
          const batchSize = 5;
          for (let i = 0; i < tableNames.length; i += batchSize) {
            const batch = tableNames.slice(i, i + batchSize);
            Promise.all(batch.map(t => 
              tableAPI.getTableFields(t).then(res => {
                if (res.data.success) {
                  fieldsMap[t] = res.data.fields;
                }
              }).catch(() => {})
            )).then(() => {
              setTableFieldsMap(prev => ({ ...prev, ...fieldsMap }));
            });
          }
        }
      }).catch(() => {});
      
      // Load XbyX configurations in parallel
      recordAPI.getAllXbyXConfigs().then(xbyxRes => {
        if (xbyxRes.data.success && xbyxRes.data) {
          setAllXbyXConfigs(xbyxRes.data);
        }
      }).catch(() => {});
      
    } catch (err) {
      // Error loading field visibility
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = (fieldName) => {
    setFieldSettings(prev => {
      const current = prev[fieldName] || { isVisible: true, columnOrder: 0 };
      return {
        ...prev,
        [fieldName]: {
          ...current,
          isVisible: !current.isVisible
        }
      };
    });
  };

  const handleDisplayNameChange = (fieldName, value) => {
    setDisplayNames(prev => ({
      ...prev,
      [fieldName]: value || fieldName
    }));
  };

  const handleLinkChange = (fieldName, isLinked) => {
    setFieldLinks(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        isLinked,
        linkedTableName: isLinked ? prev[fieldName]?.linkedTableName : null,
        linkedFieldName: isLinked ? prev[fieldName]?.linkedFieldName : null
      }
    }));
  };

  const handleLinkedTableChange = (fieldName, linkedTableName) => {
    setFieldLinks(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        linkedTableName,
        linkedFieldName: null // Reset field when table changes
      }
    }));
  };

  const handleLinkedFieldChange = (fieldName, linkedFieldName) => {
    setFieldLinks(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        linkedFieldName
      }
    }));
  };

  const handleSave = async () => {
    try {
      // Validate that loadedFields is an array
      if (!Array.isArray(loadedFields)) {
        alert('Error: Fields data is not available. Please close and reopen the dialog.');
        return;
      }

      // Build the fields array to send to the new update-fields endpoint
      // Normalize field properties to handle different API response formats
      const fieldsToUpdate = loadedFields.map(field => {
        // Extract fieldname with fallback for different casing
        const fieldname = field.fieldname || field.fieldName || field.FieldName;
        // Extract FieldsID with fallback for different casing and column name variations
        const fieldsID = field.FieldsID || field.fieldsID || field.FIELDSID || field.id || field.ID;
        
        if (!fieldname) {
          // Skip or use field name fallback
        }
        if (!fieldsID) {
          // Skip or use id fallback
        }

        return {
          FieldsID: fieldsID,
          fieldname: fieldname,
          description: displayNames[fieldname] || field.description || '',
          ordering: field.ordering || 0,
          addable: fieldSettings[fieldname]?.isVisible !== false,
          editable: fieldSettings[fieldname]?.isVisible !== false,
          shortViewable: fieldSettings[fieldname]?.isVisible !== false,
          searchable: fieldSettings[fieldname]?.isVisible !== false,
          type: field.type || null,
          sqlSource: field.SQLSource || field.sqlSource || null,
          sqlDisplay: sqlDisplays[fieldname] || null,
          sqlWhere: field.SQLWhere || field.sqlWhere || null,
          encrypted: field.Encrypted || field.encrypted || false,
          defaultValue: field.DefaultValue || field.defaultValue || null,
          subRecordID: subRecordIDs[fieldname] || null
        };
      });
      
      // Use the tableID stored in ref
      const actualTableID = tableIDRef.current;
      
      if (actualTableID) {
        const response = await mgbSetupAPI.updateFields(actualTableID, fieldsToUpdate);
        if (response.data.success) {
          // Field configuration saved successfully
        }
      } else {
        await tableAPI.saveFieldVisibility(tableName, fieldSettings);
        await tableAPI.saveFieldDisplayNames(tableName, displayNames);
        const linksArray = loadedFields.map(field => ({
          fieldName: field.fieldname || field.fieldName,
          isLinked: fieldLinks[field.fieldname || field.fieldName]?.isLinked || false,
          linkedTableName: fieldLinks[field.fieldname || field.fieldName]?.linkedTableName || null,
          linkedFieldName: fieldLinks[field.fieldname || field.fieldName]?.linkedFieldName || null
        }));
        await tableAPI.saveFieldLinks(tableName, linksArray);
      }
      
      if (onSave) {
        onSave(fieldSettings);
      }
      onHide();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save field settings';
      alert(errorMsg);
    }
  };

  const getLinkedFieldsForTable = (linkedTableName) => {
    return tableFieldsMap[linkedTableName] || [];
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Configure Fields - {tableName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <p>Loading field settings...</p>
        ) : loadedFields && loadedFields.length > 0 ? (
          <div className="field-config-list">
            <p className="text-muted mb-3">Configure visibility, display names, and field links:</p>
            <Accordion>
              {loadedFields.map((field, index) => {
                // Normalize field property name
                const fieldName = field.fieldname || field.fieldName;
                const currentLink = fieldLinks[fieldName] || {};
                return (
                  <Accordion.Item eventKey={index.toString()} key={fieldName}>
                    <Accordion.Header>
                      <Form.Check
                        type="checkbox"
                        id={`field-${fieldName}`}
                        checked={fieldSettings[fieldName]?.isVisible || false}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleVisibility(fieldName);
                        }}
                        className="me-2"
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: fieldSettings[fieldName]?.isVisible ? 'bold' : 'normal' }}>
                        {fieldName}
                        <small className="text-muted ms-2">({field.dataType})</small>
                      </span>
                      {displayNames[fieldName] && (
                        <small className="text-success ms-2">
                          → {displayNames[fieldName]}
                        </small>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Display Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={fieldName}
                          value={displayNames[fieldName] || ''}
                          onChange={(e) => handleDisplayNameChange(fieldName, e.target.value)}
                          disabled={!(fieldSettings[fieldName]?.isVisible)}
                        />
                        <Form.Text className="text-muted">
                          Leave empty to use field name as display name
                        </Form.Text>
                      </Form.Group>

                      {/* SQL Display Field */}
                      <Form.Group className="mb-3">
                        <Form.Label>SQL Display Field</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., GroupName"
                          value={sqlDisplays[fieldName] || ''}
                          onChange={(e) => setSqlDisplays({ ...sqlDisplays, [fieldName]: e.target.value })}
                        />
                        <Form.Text className="text-muted">
                          The field to display when showing linked values
                        </Form.Text>
                      </Form.Group>

                      {/* Sub Record ID Field (Cross-Table) - Only show for multiselect fields */}
                      {(() => {
                        const isMultiselect = field.type && field.type.toLowerCase() === 'multiselect';
                        return isMultiselect && (
                          <Form.Group className="mb-3">
                            <Form.Label>Cross-Table Configuration</Form.Label>
                            <Form.Select
                              value={subRecordIDs[fieldName] || ''}
                              onChange={(e) => setSubRecordIDs({ ...subRecordIDs, [fieldName]: e.target.value ? parseInt(e.target.value) : null })}
                            >
                              <option value="">-- None (regular field) --</option>
                              {allXbyXConfigs && allXbyXConfigs.map(config => (
                                <option key={config.MGB_XbyXID} value={config.MGB_XbyXID}>
                                  {config.LeftTable} ↔ {config.RightTable} ({config.Description || 'No description'})
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Select a cross-table configuration for multi-select fields
                            </Form.Text>
                          </Form.Group>
                        );
                      })()}

                      {/* Field Linking Section */}
                      {fieldSettings[fieldName]?.isVisible && (
                        <div className="field-linking-section">
                          <Form.Group className="mb-3">
                            <Form.Label>Link to Another Table?</Form.Label>
                            <div className="link-radio-group">
                              <Form.Check
                                inline
                                type="radio"
                                label="No"
                                name={`link-${fieldName}`}
                                value="no"
                                checked={!currentLink.isLinked}
                                onChange={() => handleLinkChange(fieldName, false)}
                              />
                              <Form.Check
                                inline
                                type="radio"
                                label="Yes"
                                name={`link-${fieldName}`}
                                value="yes"
                                checked={currentLink.isLinked}
                                onChange={() => handleLinkChange(fieldName, true)}
                              />
                            </div>
                          </Form.Group>

                          {currentLink.isLinked && (
                            <div className="link-dropdowns">
                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label size="sm">Select Table</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={currentLink.linkedTableName || ''}
                                      onChange={(e) => handleLinkedTableChange(fieldName, e.target.value)}
                                    >
                                      <option value="">-- Select a table --</option>
                                      {allTables.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                      ))}
                                    </Form.Select>
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label size="sm">Select Field</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={currentLink.linkedFieldName || ''}
                                      onChange={(e) => handleLinkedFieldChange(fieldName, e.target.value)}
                                      disabled={!currentLink.linkedTableName}
                                    >
                                      <option value="">-- Select a field --</option>
                                      {currentLink.linkedTableName && getLinkedFieldsForTable(currentLink.linkedTableName).map(f => (
                                        <option key={f.fieldName} value={f.fieldName}>{f.fieldName}</option>
                                      ))}
                                    </Form.Select>
                                  </Form.Group>
                                </Col>
                              </Row>
                            </div>
                          )}
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </div>
        ) : (
          <p className="text-muted">No fields available for this table.</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          Save Settings
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default FieldConfigPanel;
